import React, { useEffect, useState, useCallback } from 'react';

/**
 * Jednoduchý lightbox / galéria.
 *  - ESC zatvorí
 *  - šípky ← → prelistujú
 *  - klik mimo obrázku zatvorí
 *  - blokuje scroll body keď je otvorený
 */
function Lightbox({ images, startIndex = 0, onClose }) {
    const [index, setIndex] = useState(startIndex);

    const next = useCallback(
        () => setIndex((i) => (i + 1) % images.length),
        [images.length]
    );
    const prev = useCallback(
        () => setIndex((i) => (i - 1 + images.length) % images.length),
        [images.length]
    );

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') next();
            if (e.key === 'ArrowLeft') prev();
        };
        document.addEventListener('keydown', handleKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [next, prev, onClose]);

    return (
        <div
            className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 animate__animated animate__fadeIn animate__faster"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            {/* Counter */}
            <div className="absolute top-4 left-4 text-white text-sm font-medium tracking-wider bg-black/40 px-3 py-1 rounded-full">
                {index + 1} / {images.length}
            </div>

            {/* Close */}
            <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="absolute top-4 right-4 text-white/80 hover:text-red-500 transition-colors duration-200 bg-black/40 hover:bg-black/60 rounded-full p-2"
                aria-label="Zatvoriť galériu"
            >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Prev */}
            <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-red-500 transition-colors duration-200 bg-black/40 hover:bg-black/60 rounded-full p-3"
                aria-label="Predchádzajúca fotka"
            >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Next */}
            <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-red-500 transition-colors duration-200 bg-black/40 hover:bg-black/60 rounded-full p-3"
                aria-label="Ďalšia fotka"
            >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Image */}
            <img
                key={index}
                src={images[index]}
                alt={`Fotka ${index + 1} z ${images.length}`}
                className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl animate__animated animate__fadeIn animate__faster"
                onClick={(e) => e.stopPropagation()}
            />

            {/* Thumbnail strip */}
            <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[95vw] overflow-x-auto py-2 px-3 bg-black/40 rounded-xl no-scrollbar"
                onClick={(e) => e.stopPropagation()}
            >
                {images.map((img, i) => (
                    <button
                        key={i}
                        onClick={() => setIndex(i)}
                        className={`flex-shrink-0 w-14 h-14 rounded overflow-hidden border-2 transition-all duration-200 ${
                            i === index ? 'border-red-500 opacity-100 scale-110' : 'border-transparent opacity-50 hover:opacity-100'
                        }`}
                        aria-label={`Zobraziť fotku ${i + 1}`}
                    >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Lightbox;
